import { TranslationKey } from '../translation/TranslationKey';
import { translate } from '../translation/Translator';
import { UnitType, UnitSystem, UnitOfMeasure, MetricUnit, Unit } from './unitOfMeasure';

export namespace UnitOfMeasureUtil {
  export function toMetricQuantity(uom: UnitOfMeasure, unitQuantity: string) {
    const metricUnit = MetricUnit.value(uom.unit);
    const number = parseInt(unitQuantity) / Math.pow(10, metricUnit);
    return Number(number.toFixed(unitQuantity.length)).toString();
  }

  function getMetricPrefix(unit?: MetricUnit) {
    switch (unit) {
      case MetricUnit.nano:
        return 'n';
      case MetricUnit.micro:
        return '\u03BC';
      case MetricUnit.milli:
        return 'm';
      case MetricUnit.centi:
        return 'c';
      case MetricUnit.deca:
        return 'da';
      case MetricUnit.kilo:
        return 'k';
      case MetricUnit.hecto:
        return 'h';
      case MetricUnit.mega:
        return 'M';
      case MetricUnit.giga:
        return 'G';
      default:
        return '';
    }
  }

  export function getSuffix(uom?: UnitOfMeasure) {
    if (!uom) return '';

    if (uom.system === UnitSystem.metric) {
      switch (uom.unitType) {
        case UnitType.length:
          return getMetricPrefix(uom.unit) + 'm';
        case UnitType.volume:
          return getMetricPrefix(uom.unit) + 'l';
        case UnitType.mass:
          if (uom.unit === MetricUnit.mega) return 'tonne';
          return getMetricPrefix(uom.unit) + 'g';
        case UnitType.day:
          return 'day(s)';
        case UnitType.m2:
          return 'm\u00B2';
        case UnitType.m3:
          return 'm\u00B3';
        case UnitType.hour:
          return 'hour(s)';
        case UnitType.vnt:
          return 'VNT';
        case UnitType.pack:
          return 'PACK';
        case UnitType.set:
          return 'KOMPL';
        case UnitType.unit:
        case UnitType.pc:
          return 'PCS';
      }
    }

    return '';
  }

  export function isUnitTypeQuantity(unitType: UnitType) {
    return (
      unitType === UnitType.quantity ||
      unitType === UnitType.unit ||
      unitType === UnitType.pc ||
      unitType === UnitType.car ||
      unitType === UnitType.pal
    );
  }

  export function unitTypeToString(type: UnitType): string {
    switch (type) {
      case UnitType.m2:
        return 'm\u00B2';
      case UnitType.m3:
        return 'm\u00B3';
      case UnitType.pc:
        return translate({ key: TranslationKey.pc });
      case UnitType.car:
        return translate({ key: TranslationKey.car });
      case UnitType.pal:
        return translate({ key: TranslationKey.pal });
      default:
        return translate({ key: type });
    }
  }

  export const needsUnit = (unitType?: UnitType) => {
    return (
      unitType == UnitType.quantity ||
      unitType == UnitType.unit ||
      unitType == UnitType.day ||
      unitType == UnitType.set ||
      unitType == UnitType.m2 ||
      unitType == UnitType.m3 ||
      unitType == UnitType.pack ||
      unitType == UnitType.hour ||
      unitType == UnitType.vnt ||
      unitType === UnitType.pc ||
      unitType === UnitType.car ||
      unitType === UnitType.pal
    );
  };

  export function unitValues(unitOfMeasure: UnitOfMeasure) {
    return Array.from(Object.keys(MetricUnit));
  }

  export function defaultUnit(unitOfMeasure: UnitOfMeasure) {
    return MetricUnit.base;
  }

  export function unitText(unit: Unit, unitOfMeasure: UnitOfMeasure = UnitOfMeasure.default()): string {
    const text = unit === MetricUnit.base ? `${unitSuffix(unitOfMeasure)}` : `${unit}${unitSuffix(unitOfMeasure)}`;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  function unitSuffix(unitOfMeasure: UnitOfMeasure) {
    if (unitOfMeasure.system === UnitSystem.metric) {
      if (
        unitOfMeasure.unitType === UnitType.quantity ||
        unitOfMeasure.unitType === UnitType.unit ||
        unitOfMeasure.unitType === UnitType.day ||
        unitOfMeasure.unitType === UnitType.set ||
        unitOfMeasure.unitType === UnitType.m2 ||
        unitOfMeasure.unitType === UnitType.m3 ||
        unitOfMeasure.unitType === UnitType.pack ||
        unitOfMeasure.unitType === UnitType.hour ||
        unitOfMeasure.unitType === UnitType.vnt
      ) {
        return '';
      }

      switch (unitOfMeasure.unitType) {
        case UnitType.length:
          return translate({ key: TranslationKey.meter });
        case UnitType.mass:
          return translate({ key: TranslationKey.gram });
        case UnitType.volume:
          return translate({ key: TranslationKey.liter });
        default:
          return '';
      }
    } else {
      return '';
    }
  }
}
